import { Injectable } from '@angular/core';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { Events } from '@df/core/service/event';
import isEmpty from 'lodash-es/isEmpty';
import {
    IListingFilterEvent,
    Listing,
    LISTING_SET_FILTERS,
    LISTING_UPDATE_EVENT,
    LISTING_UPDATE_TOTALS_EVENT,
    LISTING_UPDATE_VISIBLE_MODELS
} from '@df/catalog-ui/listing/listing';
import { DfEventService } from '@df/core/service/df-event';

declare module '@df/catalog/product/product' {
    interface IProductDataV2 {
        isBanner?: boolean;
    }
}

@Injectable({
    providedIn: 'root'
})
export class IsBannerPluginService {
    constructor(events: Events, dfEventService: DfEventService) {
        events
            .on(LISTING_UPDATE_TOTALS_EVENT)
            .subscribe((listing: Listing<Product<IProductDataV2>>) => this.onListingUpdateTotals(listing));
        events
            .on(LISTING_UPDATE_VISIBLE_MODELS)
            .subscribe((listing: Listing<Product<IProductDataV2>>) => this.onListingUpdateVisibleModels(listing));
        events.on<IListingFilterEvent<Product<IProductDataV2>>>(LISTING_SET_FILTERS).subscribe(event => this.onListingSetFilters(event));

        // module plugin to GTM 2 service
        dfEventService.before(_event => {
            const event = _event as any; // as any cause we don't have types for this event in GTM 1
            switch (event.id) {
                case 'dfGtmItemListView':
                    const eventData = event.data;
                    const filteredItems = eventData.items.filter(item => {
                        if (item.product?.data?.isBanner) {
                            const position = item.position;
                            // reduce position of the items after the one
                            eventData.items.forEach(i => {
                                if (i.position > position) {
                                    i.position--;
                                }
                            });
                            // skip it
                            return false;
                        }
                        return true;
                    });
                    event.data.items = filteredItems;
                    break;
            }
        });
    }

    isSorted(listing: Listing): boolean {
        const selection = listing.sort?.getSelection();
        return selection && !selection.default ? true : false;
    }

    protected onListingUpdateTotals(listing: Listing<Product<IProductDataV2>>): void {
        if (listing.type === 'product') {
            listing.total = this.purify(listing.getFilteredModels()).length;
        }
    }

    protected onListingUpdateVisibleModels(listing: Listing<Product<IProductDataV2>>): void {
        if (listing.type === 'product') {
            listing.visibleQty = this.purify(listing.models).length;
        }
    }

    protected purify(models: Product<IProductDataV2>[]): Product<IProductDataV2>[] {
        return models?.filter(model => model.data && !model.data?.isBanner) || [];
    }

    private onListingSetFilters(event: IListingFilterEvent<Product<IProductDataV2>>): void {
        if (event.listing.type === 'product') {
            // if filters are applied
            if (
                event.listing.filters.find(filter => {
                    let selectionOverride = event.overrides?.find(i => i.filter === filter)?.selection;
                    // if override is false then skip filter completely
                    return selectionOverride !== false && (selectionOverride || filter.hasSelection);
                })
            ) {
                event.models = this.purify(event.models);
            }
        }
    }
}
